import { useEffect, useRef, useState } from 'react';

import { MobileMenuComponentType } from '@core/types';

import { markChatChannelAsRead, onDemandResetUnreadChatMessages, useAppDispatch, useAppSelector } from '@online-webinar/store';
import { useTranslation } from 'react-i18next';
import { IconSlides } from '../MobileMenu/atoms';
import { IconChapters } from '../MobileMenu/atoms/IconChapters';
import { IconChat } from '../MobileMenu/atoms/IconChat';
import { IconVideo } from '../MobileMenu/atoms/IconVideo';
import { MenuItemProps } from '../MobileMenu/molecules/MenuItem/types';
import { MobileMenu } from '../MobileMenu/organisms';

interface MobileMenuNavigatorProps {
    hasSlides: boolean;
    activeSlideNumber: number | null;
    hasChapters: boolean;
    isLandscape: boolean;
    unreadChatMessages: number;
    onItemClick: (componentType: MobileMenuComponentType) => void;
}

export const MobileMenuNavigator = ({ onItemClick, hasSlides, activeSlideNumber, hasChapters, isLandscape, unreadChatMessages }: MobileMenuNavigatorProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [menuItemsData, setMenuItemsData] = useState<MenuItemProps[]>([]);
    const [activeMenuItem, setActiveMenuItem] = useState<MobileMenuComponentType>(MobileMenuComponentType.SLIDES);
    const [slideIndicatorActive, setSlideIndicatorActive] = useState<boolean>(false);
    const [chatIndicatorActive, setChatIndicatorActive] = useState<boolean>(false);
    // Tracks whether the user has ever deliberately clicked a menu item, so we can tell a genuine
    // Chat selection apart from Chat only being active because it's the not-yet-resolved default
    // (hasSlides starts false in every parent and flips true once slide data has loaded).
    const hasUserSelectedMenuItem = useRef(false);

    const menuItemClick = (type: MobileMenuComponentType): void => {
        hasUserSelectedMenuItem.current = true;
        setActiveMenuItem(type);
        if (type === MobileMenuComponentType.SLIDES) {
            setSlideIndicatorActive(false);
        }
        if (type === MobileMenuComponentType.CHAT) {
            setChatIndicatorActive(false);
            dispatch(markChatChannelAsRead(''));
            dispatch(onDemandResetUnreadChatMessages());
        }
    };

    useEffect(() => {
        onItemClick(activeMenuItem);
    }, [activeMenuItem]);

    useEffect(() => {
        const menuItemsData: MenuItemProps[] = [
            {
                label: t('Player.Menu.Chat'),
                componentType: MobileMenuComponentType.CHAT,
                onItemClick: menuItemClick,
                active: activeMenuItem === MobileMenuComponentType.CHAT,
                indicatorActive: chatIndicatorActive,
                indicatorValue: unreadChatMessages,
                indicatorLabel: t('Player.Menu.UnreadMessages', { count: unreadChatMessages }),
                icon: <IconChat />
            }
        ];

        // Add the chapters menu item only if there are chapters
        if (hasChapters) {
            menuItemsData.unshift({
                label: t('Player.Menu.Chapters'),
                componentType: MobileMenuComponentType.CHAPTER,
                onItemClick: menuItemClick,
                active: activeMenuItem === MobileMenuComponentType.CHAPTER,
                indicatorActive: false,
                icon: <IconChapters />
            });
        }

        // Add the slides menu item only if there are slides
        if (hasSlides) {
            menuItemsData.unshift({
                label: t('Player.Menu.Slides'),
                componentType: MobileMenuComponentType.SLIDES,
                onItemClick: menuItemClick,
                active: activeMenuItem === MobileMenuComponentType.SLIDES,
                indicatorActive: slideIndicatorActive,
                indicatorLabel: t('Player.Menu.NewSlide'),
                icon: <IconSlides />
            });
        }

        // Add a menu item for video when the orientation is landscape
        if (isLandscape) {
            menuItemsData.unshift({
                label: t('Player.Menu.Video'),
                componentType: MobileMenuComponentType.VIDEO,
                onItemClick: menuItemClick,
                active: activeMenuItem === MobileMenuComponentType.VIDEO,
                indicatorActive: false,
                icon: <IconVideo />
            });
        }

        setMenuItemsData(menuItemsData);
    }, [isLandscape, activeMenuItem, slideIndicatorActive, chatIndicatorActive, unreadChatMessages, activeSlideNumber, hasChapters, hasSlides]);

    useEffect(() => {
        if (isLandscape) {
            setActiveMenuItem(MobileMenuComponentType.VIDEO);
        } else {
            setActiveMenuItem(hasSlides ? MobileMenuComponentType.SLIDES : MobileMenuComponentType.CHAT);
        }
    }, [isLandscape]);

    useEffect(() => {
        // hasSlides can change mid-session (slides added/removed live, or the on-demand timeline
        // advancing). Don't switch away from Chat if the user deliberately selected it, so we
        // don't interrupt someone mid-message — but Chat being active only because it's the
        // not-yet-resolved default (see hasUserSelectedMenuItem) should still be corrected.
        const isDeliberateChatSelection = activeMenuItem === MobileMenuComponentType.CHAT && hasUserSelectedMenuItem.current;
        if (isLandscape || isDeliberateChatSelection) return;
        setActiveMenuItem(hasSlides ? MobileMenuComponentType.SLIDES : MobileMenuComponentType.CHAT);
    }, [hasSlides]);

    useEffect(() => {
        // A slide change is only news when the slides panel is not the one being viewed. Deliberately not
        // dependent on activeMenuItem: leaving the slides panel must not turn the indicator on.
        if (activeMenuItem !== MobileMenuComponentType.SLIDES) {
            setSlideIndicatorActive(true);
        }
    }, [activeSlideNumber]);

    useEffect(() => {
        // Covers every way the slides panel becomes active, not only a click on its menu item
        if (activeMenuItem === MobileMenuComponentType.SLIDES) {
            setSlideIndicatorActive(false);
        }
    }, [activeMenuItem]);

    useEffect(() => {
        if (activeMenuItem !== MobileMenuComponentType.CHAT) {
            setChatIndicatorActive(unreadChatMessages > 0);
        }

        if (activeMenuItem === MobileMenuComponentType.CHAT) {
            setChatIndicatorActive(false);
            dispatch(markChatChannelAsRead(''));
            dispatch(onDemandResetUnreadChatMessages());
        }
    }, [unreadChatMessages, activeMenuItem]);

    return <MobileMenu isLandscape={isLandscape} menuItems={menuItemsData} />;
};
